/**
 * eBay-style Category Mega Menu
 *
 * Features:
 * - Two-column layout with popular and more categories
 * - Promotional banner on the right
 * - Appears on hover
 */

import { Link } from 'react-router-dom';
import { cn } from '../lib/utils';
import { CATEGORY_DATA } from '../lib/category-data';

interface CategoryMegaMenuProps {
  categoryId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function CategoryMegaMenu({ categoryId, isOpen, onClose }: CategoryMegaMenuProps) {
  const category = CATEGORY_DATA[categoryId];

  if (!category || !isOpen) return null;

  return (
    <div className="bg-white border-b border-gray-200 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-8">
          {/* Popular Categories */}
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Most popular categories
            </h3>
            <ul className="space-y-2">
              {category.popularCategories.map((sub) => (
                <li key={sub.id}>
                  <Link
                    to={`/category/${category.slug}/${sub.slug}`}
                    className="text-sm text-gray-600 hover:text-primary hover:underline"
                    onClick={onClose}
                  >
                    {sub.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* More Categories */}
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              More categories
            </h3>
            <ul className="space-y-2">
              {category.moreCategories.map((sub) => (
                <li key={sub.id}>
                  <Link
                    to={`/category/${category.slug}/${sub.slug}`}
                    className="text-sm text-gray-600 hover:text-primary hover:underline"
                    onClick={onClose}
                  >
                    {sub.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Promo Banner */}
          {category.promo && (
            <div className="w-80 flex-shrink-0">
              <Link
                to={category.promo.ctaLink}
                className={cn(
                  'block h-full rounded-xl overflow-hidden',
                  category.promo.bgColor
                )}
                onClick={onClose}
              >
                <div className="p-6 h-full flex flex-col justify-center">
                  <h4 className="text-2xl font-bold text-white mb-2">
                    {category.promo.title}
                  </h4>
                  <p className="text-white/90 mb-4">
                    {category.promo.subtitle}
                  </p>
                  <span className="inline-flex items-center px-4 py-2 bg-white text-gray-900 text-sm font-semibold rounded-full w-fit">
                    {category.promo.ctaText}
                  </span>
                </div>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
